import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import AuthShell from "../components/auth/AuthShell";
import PasswordChecklist from "../components/auth/PasswordChecklist";
import { AppAlert, AppButton, AppField } from "../components/ui/AppField";
import { THEME } from "../theme";
import { isSignupPasswordValid } from "../utils/passwordRules";
import { isValidRwandanPhone } from "../utils/paymentHelpers";
import {
  clearAppAuthError,
  loginAppUser,
  selectAppAuthError,
  selectAppAuthLoading,
  selectAppIsAuthenticated,
  selectAppRequiresVerification,
  selectAppUser,
  selectAppVerificationEmail,
  selectAppVerificationPhone,
  signupAppUser,
} from "../redux/appAuthSlice";
import {
  selectIsAuthenticated as selectAdminIsAuthenticated,
} from "../../redux/slices/authSlice";
import {
  isStaffRole,
  syncStaffAdminSession,
} from "../../utils/unifiedAuth";
import { isAdminHost } from "../../utils/adminHost";
import { HOME_PATH } from "../config/products";

const ContactMethodToggle = ({ value, onChange }) => (
  <div className="app-contact-toggle" role="tablist" aria-label="Contact method">
    {[
      { id: "email", label: "Email" },
      { id: "phone", label: "Phone" },
    ].map((option) => (
      <button
        key={option.id}
        type="button"
        role="tab"
        aria-selected={value === option.id}
        className={`app-contact-toggle-btn${
          value === option.id ? " app-contact-toggle-btn-active" : ""
        }`}
        onClick={() => onChange(option.id)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const Auth = ({ initialMode = "login" }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const loading = useSelector(selectAppAuthLoading);
  const error = useSelector(selectAppAuthError);
  const isAuthenticated = useSelector(selectAppIsAuthenticated);
  const user = useSelector(selectAppUser);
  const adminIsAuthenticated = useSelector(selectAdminIsAuthenticated);
  const requiresVerification = useSelector(selectAppRequiresVerification);
  const verificationEmail = useSelector(selectAppVerificationEmail);
  const verificationPhone = useSelector(selectAppVerificationPhone);

  const [mode, setMode] = useState(initialMode);
  const [contactMethod, setContactMethod] = useState("email");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState("");
  const [authSplashPending, setAuthSplashPending] = useState(false);

  const returnTo = location.state?.from || HOME_PATH;
  const loginMessage = location.state?.message || "";
  const isSignup = mode === "signup";
  const onAdminHost = isAdminHost();

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (mode !== "signup") return;
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref") || params.get("referral");
    if (ref?.trim()) setReferralCode(ref.trim());
  }, [mode, location.search]);

  useEffect(() => {
    if (!isAuthenticated || !user || mode !== "login" || authSplashPending) return;

    // Admin console only on admin.betbond.app — never from the main site.
    if (onAdminHost && isStaffRole(user.role)) {
      if (adminIsAuthenticated) {
        navigate("/admin", { replace: true });
      }
      return;
    }

    navigate(returnTo, { replace: true });
  }, [
    isAuthenticated,
    user,
    adminIsAuthenticated,
    mode,
    authSplashPending,
    navigate,
    returnTo,
    onAdminHost,
  ]);

  useEffect(() => {
    if (!requiresVerification) return;
    if (!verificationEmail && !verificationPhone) return;

    navigate("/app/verify", {
      replace: true,
      state: {
        email: verificationEmail || "",
        phoneNumber: verificationPhone || "",
        contactMethod: verificationEmail ? "email" : "phone",
        from: returnTo,
      },
    });
  }, [
    requiresVerification,
    verificationEmail,
    verificationPhone,
    navigate,
    returnTo,
  ]);

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setLocalError("");
    dispatch(clearAppAuthError());
  };

  const handleContactMethodChange = (nextMethod) => {
    setContactMethod(nextMethod);
    setLocalError("");
    dispatch(clearAppAuthError());
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError("");

    const sanitizedEmail = email.trim().toLowerCase();
    const sanitizedPhone = phoneNumber.trim();
    const sanitizedPassword = password.trim();

    if (contactMethod === "email") {
      if (!sanitizedEmail || !sanitizedEmail.includes("@")) {
        setLocalError("Enter a valid email.");
        return;
      }
    } else if (!isValidRwandanPhone(sanitizedPhone)) {
      setLocalError("Enter a valid Rwandan phone number.");
      return;
    }

    if (sanitizedPassword.length < 6) {
      setLocalError("Password must be at least 6 characters.");
      return;
    }

    dispatch(clearAppAuthError());
    setAuthSplashPending(true);

    try {
      const payload = { password: sanitizedPassword };
      if (contactMethod === "email") payload.email = sanitizedEmail;
      else payload.phoneNumber = sanitizedPhone;

      const result = await dispatch(loginAppUser(payload)).unwrap();

      if (onAdminHost && isStaffRole(result.user?.role)) {
        await syncStaffAdminSession(dispatch, {
          email: sanitizedEmail,
          password: sanitizedPassword,
        });
        navigate("/admin", { replace: true });
        return;
      }

      navigate("/app/welcome", { replace: true, state: { to: returnTo } });
    } catch {
      setAuthSplashPending(false);
      /* slice sets error state */
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLocalError("");

    const sanitizedName = name.trim();
    const sanitizedEmail = email.trim().toLowerCase();
    const sanitizedPhone = phoneNumber.trim();
    const sanitizedPassword = password.trim();

    if (!sanitizedName) {
      setLocalError("Enter your name.");
      return;
    }

    if (contactMethod === "email") {
      if (!sanitizedEmail || !sanitizedEmail.includes("@")) {
        setLocalError("Enter a valid email.");
        return;
      }
    } else if (!isValidRwandanPhone(sanitizedPhone)) {
      setLocalError("Enter a valid Rwandan phone number (e.g. 0781234567).");
      return;
    }

    if (!isSignupPasswordValid(sanitizedPassword)) {
      return;
    }

    dispatch(clearAppAuthError());
    setAuthSplashPending(true);

    try {
      const payload = {
        name: sanitizedName,
        password: sanitizedPassword,
      };
      if (contactMethod === "email") payload.email = sanitizedEmail;
      else payload.phoneNumber = sanitizedPhone;

      if (referralCode.trim()) {
        payload.referralCode = referralCode.trim();
      }

      const result = await dispatch(signupAppUser(payload)).unwrap();
      if (result.immediate && result.token) {
        navigate("/app/welcome", { replace: true, state: { to: returnTo } });
      }
    } catch {
      setAuthSplashPending(false);
      /* slice sets error state */
    }
  };

  const displayError = localError || error;

  const passwordToggle = (
    <button
      type="button"
      onClick={() => setShowPassword((v) => !v)}
      className="mr-3 p-1 text-white/40 hover:text-white transition-colors"
      aria-label={showPassword ? "Hide password" : "Show password"}
    >
      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
    </button>
  );

  const contactField =
    contactMethod === "email" ? (
      <AppField
        id={isSignup ? "auth-email-signup" : "auth-email-login"}
        label="Email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (error) dispatch(clearAppAuthError());
          if (localError) setLocalError("");
        }}
      />
    ) : (
      <AppField
        id={isSignup ? "auth-phone-signup" : "auth-phone-login"}
        label="Phone number"
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="0781234567"
        value={phoneNumber}
        onChange={(e) => {
          setPhoneNumber(e.target.value);
          if (error) dispatch(clearAppAuthError());
          if (localError) setLocalError("");
        }}
      />
    );

  return (
    <AuthShell
      title={isSignup ? "Create account" : "Log in"}
      subtitle={
        isSignup
          ? "Join BetBond to challenge friends on a match, chat, and climb the board."
          : onAdminHost
            ? "Staff sign-in for the BetBond admin console."
            : "Sign in with email or phone."
      }
      footer={
        <Link
          to={HOME_PATH}
          className="app-btn-ghost flex h-12 w-full items-center justify-center rounded-lg text-[14px] font-extrabold tracking-tight text-white"
        >
          Continue as guest
        </Link>
      }
    >
      {isSignup ? (
        <form onSubmit={handleSignup} className="space-y-5">
          {displayError && <AppAlert>{displayError}</AppAlert>}

          <AppField
            id="auth-name"
            label="Full name"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) dispatch(clearAppAuthError());
              if (localError) setLocalError("");
            }}
          />

          <ContactMethodToggle value={contactMethod} onChange={handleContactMethodChange} />
          {contactField}

          <AppField
            id="auth-password-signup"
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) dispatch(clearAppAuthError());
              if (localError) setLocalError("");
            }}
            suffix={passwordToggle}
          />
          <PasswordChecklist password={password} />

          <AppField
            id="auth-referral-signup"
            label="Referral code (optional)"
            type="text"
            autoComplete="off"
            placeholder="Enter a friend's invite code"
            value={referralCode}
            onChange={(e) => {
              setReferralCode(e.target.value);
              if (error) dispatch(clearAppAuthError());
              if (localError) setLocalError("");
            }}
          />

          <AppButton type="submit" disabled={loading}>
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Creating account
              </span>
            ) : (
              "Create account"
            )}
          </AppButton>

          <div
            className="pt-5 mt-1 border-t space-y-3"
            style={{ borderColor: "rgba(255, 255, 255, 0.06)" }}
          >
            <p className="text-center text-[13px]" style={{ color: THEME.text.tertiary }}>
              Already have an account?
            </p>
            <button
              type="button"
              onClick={() => switchMode("login")}
              className="app-btn-ghost flex h-12 w-full items-center justify-center rounded-lg text-[14px] font-extrabold tracking-tight text-white"
            >
              Log in
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleLogin} className="space-y-5">
          {loginMessage ? (
            <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-[12px] text-emerald-200">
              {loginMessage}
            </p>
          ) : null}
          {displayError && <AppAlert>{displayError}</AppAlert>}

          <ContactMethodToggle value={contactMethod} onChange={handleContactMethodChange} />
          {contactField}

          <AppField
            id="auth-password-login"
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) dispatch(clearAppAuthError());
              if (localError) setLocalError("");
            }}
            suffix={passwordToggle}
          />

          <div className="flex justify-end">
            <Link
              to="/app/forgot-password"
              className="text-[12px] font-semibold text-[#64b5f6] hover:text-white transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <AppButton type="submit" disabled={loading}>
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                Signing in
              </span>
            ) : (
              "Log in"
            )}
          </AppButton>

          <div
            className="pt-5 mt-1 border-t space-y-3"
            style={{ borderColor: "rgba(255, 255, 255, 0.06)" }}
          >
            <p className="text-center text-[13px]" style={{ color: THEME.text.tertiary }}>
              Don&apos;t have an account?
            </p>
            <button
              type="button"
              onClick={() => switchMode("signup")}
              className="app-btn-ghost flex h-12 w-full items-center justify-center rounded-lg text-[14px] font-extrabold tracking-tight text-white"
            >
              Create account
            </button>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

export default Auth;
